import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AttachmentIcon from "@mui/icons-material/Attachment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getLawyers } from "../../services/LawyerService";
import { getAttachmentTypes } from "../../services/AttachmentTypeService";
import { downloadFilePost } from "../../services/ReportService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import LegalFileStageFilter from "../General/LegalFileStageFilter";
import LegalFileTypeFilter from "../General/LegalFileTypeFilter";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";
import { useToast } from "../../context/ToastContext";

export default function ReportAttachment() {
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [uploadedStartDate, setUploadedStartDate] = useState(null);
  const [uploadedEndDate, setUploadedEndDate] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [selectedAttachmentTypes, setSelectedAttachmentTypes] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [lawyers, setLawyers] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    async function fetchOptions() {
      setLoadingOptions(true);
      try {
        const [resLawyers, resAttachmentTypes] = await Promise.all([
          getLawyers(),
          getAttachmentTypes(),
        ]);

        setLawyers(resLawyers.data.data || []);
        setAttachmentTypes(resAttachmentTypes.data.data || []);
      } catch (error) {
        console.error("Error al cargar opciones:", error);
        showToast("Error al cargar abogados o tipos de escritos.", "error")
      } finally {
        setLoadingOptions(false);
      }
    }

    fetchOptions();
  }, [showToast]);

  const statusOptions = [
    { id: "generated", label: "GENERADO" },
    { id: "preloaded", label: "PRECARGADO" },
    { id: "uploaded", label: "CARGADO" },
    { id: "failed", label: "FALLIDO" },
  ];

  const handleGenerateReport = async () => {
    if (startDate && !endDate) {
      showToast("Debe completar la fecha de asociación 'Hasta'.", "error")
      return;
    }
    if (uploadedStartDate && !uploadedEndDate) {
      showToast("Debe completar la fecha de cargado 'Hasta'.", "error")
      return;
    }
    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast(
        "La fecha de inicio no puede ser posterior a la fecha de fin.",
      "error")
      return;
    }

    if (
      uploadedStartDate &&
      uploadedEndDate &&
      uploadedStartDate.isAfter(uploadedEndDate)
    ) {
      showToast(
        "La fecha de cargado desde no puede ser posterior a la fecha de cargado hasta.",
      "error")
      return;
    }
    setLoading(true);

    try {
      const payload = {
        status: selectedStatus.map((s) => s.id),
        lawyers: selectedLawyers.map((l) => l.id),
        attachment_types: selectedAttachmentTypes.map((t) => t.id),
        legal_file_stage_ids: selectedStages.map((s) => s.id),
        legal_file_types: selectedTypes.map((t) => t.id),
        status_tax_ids: selectedStatusTax.map((s) => s.id),
      };

      if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
      if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");

      if (uploadedStartDate) {
        payload.uploaded_start_date = uploadedStartDate.format("YYYY-MM-DD");
      }

      if (uploadedEndDate) {
        payload.uploaded_end_date = uploadedEndDate.format("YYYY-MM-DD");
      }

      const response = await downloadFilePost(
        "attachments/legal/file/export/file",
        payload,
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      a.download = `reporte_adjuntos_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar el reporte.", "error")
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <LoadingState text="Cargando filtros..." />;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
            <CardHeader
              icon={<AttachmentIcon />}
              title="Reporte de Escritos Asociados"
              description="Exporta los escritos asociados filtrando por fechas, estado, tipo de escrito y abogado."
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <DatePicker
                  label="Fecha asociación desde"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <DatePicker
                  label="Fecha asociación hasta"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <DatePicker
                  label="Fecha cargado desde"
                  value={uploadedStartDate}
                  onChange={(newValue) => setUploadedStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <DatePicker
                  label="Fecha cargado hasta"
                  value={uploadedEndDate}
                  onChange={(newValue) => setUploadedEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={statusOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedStatus}
                  onChange={(event, newValue) => setSelectedStatus(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado del Archivo"
                      placeholder="Seleccionar estado..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={attachmentTypes.filter(
                    (type) => type.status === "enabled",
                  )}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedAttachmentTypes}
                  onChange={(event, newValue) =>
                    setSelectedAttachmentTypes(newValue)
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipos de Escrito"
                      placeholder="Seleccionar tipos..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={[
                    {
                      id: "select-all",
                      name: "Seleccionar todos",
                      lastname: "",
                    },
                    ...lawyers,
                  ]}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    option.id === "select-all"
                      ? option.name
                      : `${option.name} ${option.lastname}` || option
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedLawyers}
                  onChange={(event, newValue) => {
                    const selectAllOption = newValue.find(
                      (val) => val.id === "select-all",
                    );

                    if (selectAllOption) {
                      if (selectedLawyers.length === lawyers.length) {
                        setSelectedLawyers([]);
                      } else {
                        setSelectedLawyers(lawyers);
                      }
                    } else {
                      setSelectedLawyers(newValue);
                    }
                  }}
                  renderOption={(props, option, { selected }) => {
                    const isSelectAll = option.id === "select-all";
                    const allSelected =
                      selectedLawyers.length === lawyers.length;

                    return (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          checked={isSelectAll ? allSelected : selected}
                        />
                        {isSelectAll
                          ? option.name
                          : `${option.name} ${option.lastname}` || option}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Abogados"
                      placeholder="Seleccionar..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <LegalFileStageFilter
                  value={selectedStages}
                  onChange={setSelectedStages}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <LegalFileTypeFilter
                  value={selectedTypes}
                  onChange={setSelectedTypes}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <LegalFileStatusTaxFilter
                  value={selectedStatusTax}
                  onChange={setSelectedStatusTax}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleGenerateReport}

                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Generar Reporte Escritos"
                  )}
                </Button>
              </Grid>
            </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
